import { useDrop, useDrag } from "react-dnd";
import { User, Plus, Edit2 } from "lucide-react";
import { Person } from "../../../drizzle/schema";
import { useState, useRef, useCallback } from "react";
import { PersonTooltip } from "./PersonTooltip";
import { trpc } from "../lib/trpc";
import { Input } from "./ui/input";
import { useIsMobile } from "@/hooks/useIsMobile";

const reverseStatusMap = {
  Yes: "director" as const,
  Maybe: "staff" as const,
  No: "co-director" as const,
  "Not Invited": "not-invited" as const,
};

const statusColors = {
  director: "text-emerald-700",
  staff: "text-yellow-600",
  "co-director": "text-red-700",
  "not-invited": "text-slate-500",
};

interface DistrictDirectorDropZoneProps {
  person: Person | null;
  onDrop: (personId: string, fromCampusId: string | number) => void;
  onEdit: (campusId: string | number, person: Person) => void;
  onClick: () => void;
  onShowPersonTooltip?: (
    person: Person,
    campusId: string | number,
    position?: { x: number; y: number }
  ) => void;
  centralizedTooltipOpen?: boolean;
  onAddClick: () => void;
  quickAddMode?: boolean;
  quickAddName?: string;
  onQuickAddNameChange?: (name: string) => void;
  onQuickAddSubmit?: () => void;
  onQuickAddCancel?: () => void;
  onQuickAddClick?: (e: React.MouseEvent) => void;
  quickAddInputRef?: React.RefObject<HTMLInputElement | null>;
  districtId?: string | null; // For XAN, use "National Director" instead of "District Director"
  canInteract?: boolean;
  maskIdentity?: boolean;
  /** When true, name is visible but details (status color, role, needs, tooltip) are hidden. */
  maskDetails?: boolean;
}

export function DistrictDirectorDropZone({
  person,
  onDrop,
  onEdit,
  onClick,
  onShowPersonTooltip,
  centralizedTooltipOpen = false,
  onAddClick,
  quickAddMode = false,
  quickAddName = "",
  onQuickAddNameChange,
  onQuickAddSubmit,
  onQuickAddCancel,
  onQuickAddClick: _onQuickAddClick,
  quickAddInputRef,
  districtId = null,
  canInteract = true,
  maskIdentity = false,
  maskDetails = false,
}: DistrictDirectorDropZoneProps) {
  // Hide details when either maskIdentity (fully hidden) or maskDetails (in-scope but no detail view)
  const hideDetails = maskIdentity || maskDetails;
  const isMobile = useIsMobile();
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(
    null
  );
  const [mobileTooltipOpen, setMobileTooltipOpen] = useState(false);
  const [clickTooltipOpen, setClickTooltipOpen] = useState(false);
  const longPressTimerRef = useRef<number | null>(null);
  const longPressTriggeredRef = useRef(false);
  const iconRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLDivElement>(null);

  // Fetch needs by person to get all needs (including inactive) to show met needs with checkmark
  const { data: personNeeds = [] } = trpc.needs.byPerson.useQuery(
    { personId: person?.personId || "" },
    { enabled: !!person && !hideDetails }
  );
  const personNeed = person && personNeeds.length > 0 ? personNeeds[0] : null;

  const [{ isOver: _isOver, canDrop: _canDrop }, drop] = useDrop(
    () => ({
      accept: "person",
      drop: (item: { personId: string; campusId: string | number }) => {
        if (canInteract) {
          onDrop(item.personId, item.campusId);
        }
      },
      canDrop: (item: { personId: string; campusId: string | number }) => {
        // Can't drop on itself if it's already the district director
        return canInteract && item.campusId !== "district";
      },
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [onDrop, canInteract]
  );

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: "person",
      item: person ? { personId: person.personId, campusId: "district" } : null,
      canDrag: () => !!person && canInteract,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [person, canInteract]
  );

  // Callback refs for react-dnd
  const setDropRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        drop(node);
      }
    },
    [drop]
  );

  const handleNameMouseEnter = (_e: React.MouseEvent) => {
    if (isMobile) return;
    if (person && canInteract) {
      setIsHovered(true);
      if (nameRef.current) {
        const rect = nameRef.current.getBoundingClientRect();
        setTooltipPos({ x: rect.left, y: rect.top });
      }
    }
  };

  const handleNameMouseLeave = () => {
    if (isMobile) return;
    if (clickTooltipOpen) return;
    if (canInteract) {
      setIsHovered(false);
      setTooltipPos(null);
    }
  };

  const handleNameMouseMove = (_e: React.MouseEvent) => {
    if (isMobile) return;
    if (nameRef.current && isHovered && person && canInteract) {
      const rect = nameRef.current.getBoundingClientRect();
      setTooltipPos({ x: rect.left, y: rect.top });
    }
  };

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current != null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleNamePointerDown = (e: React.PointerEvent) => {
    if (!isMobile || hideDetails || !canInteract) return;
    if (e.pointerType === "mouse") return;

    longPressTriggeredRef.current = false;
    clearLongPressTimer();

    longPressTimerRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = true;
      setIsHovered(true);
      setTooltipPos({ x: 0, y: 0 });
      setMobileTooltipOpen(true);
    }, 450);
  };

  const handleNamePointerUpOrCancel = () => {
    if (!isMobile) return;
    clearLongPressTimer();
    if (longPressTriggeredRef.current) return;
    setIsHovered(false);
    setTooltipPos(null);
    window.setTimeout(() => {
      longPressTriggeredRef.current = false;
    }, 0);
  };

  const dismissClickTooltip = () => {
    setClickTooltipOpen(false);
    setIsHovered(false);
    setTooltipPos(null);
  };

  const handleEditFromTooltip = () => {
    dismissClickTooltip();
    dismissMobileTooltip();
    onEdit("district", person!);
  };

  const dismissMobileTooltip = () => {
    setMobileTooltipOpen(false);
    setIsHovered(false);
    setTooltipPos(null);
    longPressTriggeredRef.current = false;
  };

  if (!person) {
    // In public/masked mode, hide the slot entirely when no district director exists
    if (hideDetails) {
      return null;
    }

    // Show add button when no district director (only in interactive mode)
    return (
      <div
        ref={setDropRef}
        className={`flex flex-col items-center group/person w-[60px] transition-transform -ml-3 -mt-2 ${!canInteract ? "cursor-not-allowed" : ""}`}
      >
        <div className="relative flex flex-col items-center w-[60px] group/add">
          <button
            type="button"
            disabled={!canInteract}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              if (!canInteract) return;
              onAddClick();
            }}
            className={`flex flex-col items-center w-[60px] focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 ${!canInteract ? "cursor-not-allowed" : ""}`}
          >
            {/* Plus sign in name position - clickable for quick add; min-h matches name row for alignment */}
            <div className="relative flex items-center justify-center mb-1 min-h-[1.25rem] overflow-visible">
              {canInteract && quickAddMode ? (
                <div className="relative -mt-[5px]">
                  <Input
                    ref={quickAddInputRef}
                    value={quickAddName}
                    onChange={e => onQuickAddNameChange?.(e.target.value)}
                    onKeyDown={e => {
                      e.stopPropagation();
                      if (e.key === "Enter") {
                        onQuickAddSubmit?.();
                      } else if (e.key === "Escape") {
                        onQuickAddCancel?.();
                      }
                    }}
                    onKeyUp={e => {
                      e.stopPropagation();
                    }}
                    onBlur={() => {
                      onQuickAddSubmit?.();
                    }}
                    placeholder="Full Name"
                    className="w-[9.5ch] h-[16px] text-[6px] px-1 py-0 text-center rounded-[2px] border-slate-300 focus:border-slate-400 focus:!ring-0 focus:!ring-offset-0 focus:!outline-none focus:!shadow-none focus-visible:border-slate-400 focus-visible:!ring-0 focus-visible:!ring-offset-0 focus-visible:!outline-none focus-visible:!shadow-none"
                    autoFocus
                    spellCheck={true}
                    autoComplete="off"
                  />
                </div>
              ) : canInteract ? (
                <span
                  className="relative inline-flex items-center justify-center p-0.5 rounded opacity-0 group-hover/add:opacity-100 transition-all cursor-pointer hover:bg-slate-100 hover:scale-110"
                  onClick={e => {
                    e.stopPropagation();
                  }}
                >
                  <span className="hidden md:inline text-[9px] font-medium text-slate-500 whitespace-nowrap leading-none relative -top-[2px]">
                    → Quick Add ←
                  </span>
                  <Plus className="w-3 h-3 text-black md:hidden" strokeWidth={1.5} />
                </span>
              ) : null}
            </div>
            {/* Icon - outline User only; plus is above head in name position */}
            <div
              className={`relative inline-block transition-transform ${canInteract ? "hover:scale-105 active:scale-95" : ""}`}
            >
              {!quickAddMode && (
                <span className="quick-add-hint-mobile absolute top-[-19.55px] left-1/2 -translate-x-1/2 text-[9px] text-slate-400 whitespace-nowrap pointer-events-none flex items-center">
                  → Quick Add ←
                </span>
              )}
              <User
                className="w-10 h-10 text-gray-300 transition-all"
                strokeWidth={1}
                fill="none"
                stroke="currentColor"
              />
              <User
                className="w-10 h-10 text-gray-400 absolute top-0 left-0 opacity-0 group-hover/add:opacity-100 transition-all pointer-events-none"
                strokeWidth={1}
                fill="none"
                stroke="currentColor"
              />
              <User
                className="w-10 h-10 text-gray-400 absolute top-0 left-0 opacity-0 group-hover/add:opacity-100 transition-all pointer-events-none"
                strokeWidth={0}
                fill="currentColor"
                style={{
                  filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
                }}
              />
            </div>
          </button>
          {/* Label - Absolutely positioned, shown on hover */}
          {canInteract && (
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-0.5 text-xs text-slate-500 text-center max-w-[80px] leading-tight opacity-0 group-hover/add:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              Add
            </div>
          )}
        </div>
      </div>
    );
  }

  const figmaStatus = reverseStatusMap[person.status] || "not-invited";
  const firstName = person.name?.split(" ")[0] || person.personId || "Person";
  const capitalizedFirstName =
    firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
  const truncatedName =
    capitalizedFirstName.length > 10
      ? capitalizedFirstName.slice(0, 10) + "."
      : capitalizedFirstName;

  return (
    <>
      <div
        ref={setDropRef}
        className="flex flex-col items-center group/person w-[60px] transition-transform -ml-3 -mt-2"
      >
        {/* Name Label with Edit Button */}
        <div
          ref={nameRef}
          className={`relative flex items-center justify-center mb-1 group/name w-full min-w-0 ${canInteract ? "cursor-pointer" : "cursor-not-allowed"}`}
          onClick={e => {
            if (!canInteract || !person) return;
            if (isMobile && longPressTriggeredRef.current) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            if (onShowPersonTooltip) {
              const rect = nameRef.current?.getBoundingClientRect();
              const pos =
                rect != null ? { x: rect.right, y: rect.bottom } : undefined;
              onShowPersonTooltip(person, "district", pos);
              return;
            }
            setClickTooltipOpen(true);
            setTooltipPos({ x: 0, y: 0 });
            setIsHovered(true);
          }}
          onMouseEnter={handleNameMouseEnter}
          onMouseLeave={handleNameMouseLeave}
          onMouseMove={handleNameMouseMove}
          onPointerDown={handleNamePointerDown}
          onPointerUp={handleNamePointerUpOrCancel}
          onPointerCancel={handleNamePointerUpOrCancel}
          onPointerLeave={handleNamePointerUpOrCancel}
        >
          <div className="relative inline-flex items-center justify-center">
            <div className="text-sm text-slate-600 font-semibold text-center whitespace-nowrap overflow-hidden max-w-full">
              {maskIdentity ? "\u00A0" : truncatedName}
            </div>
            {canInteract && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onEdit("district", person);
                }}
                onMouseDown={e => e.stopPropagation()}
                className="absolute left-full top-0 -translate-y-0.5 ml-1.5 opacity-0 group-hover/name:opacity-100 group-hover/person:opacity-100 transition-opacity p-0.5 hover:bg-slate-100 rounded z-10"
                title="Edit person"
              >
                <Edit2 className="w-2.5 h-2.5 text-slate-500" />
              </button>
            )}
          </div>
        </div>

        <div
          ref={node => {
            iconRef.current = node;
            if (canInteract) {
              drag(node);
            }
          }}
          className={`relative ${canInteract ? "cursor-grab active:cursor-grabbing" : "cursor-not-allowed"}`}
          style={{ opacity: isDragging ? 0.5 : 1 }}
        >
          <button
            onClick={() => {
              if (hideDetails || !canInteract) return;
              onClick();
            }}
            className={`relative transition-all ${canInteract ? "hover:scale-110 active:scale-95 cursor-pointer" : "cursor-not-allowed"}`}
          >
            {/* Gray spouse icon behind - shown when person has a spouse */}
            {!hideDetails && person.spouse && (
              <User
                className="w-10 h-10 text-slate-300 absolute top-0 left-2 pointer-events-none z-0"
                strokeWidth={1.5}
                fill="currentColor"
              />
            )}
            {/* Main person icon - solid */}
            <div
              className={`relative ${hideDetails ? "text-gray-300" : statusColors[figmaStatus]} ${!hideDetails && person.depositPaid ? "deposit-glow" : ""}`}
            >
              <User
                className={`w-10 h-10 transition-colors relative z-10`}
                strokeWidth={1.5}
                fill="currentColor"
              />
            </div>
          </button>

          {/* Role Label - Absolutely positioned, shown on hover */}
          {!hideDetails && (
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1.5 text-xs text-slate-500 text-center max-w-[80px] leading-tight whitespace-nowrap pointer-events-none opacity-0 group-hover/person:opacity-100 transition-opacity">
              {(() => {
                // For XAN, first person in header is always National Director
                if (districtId === "XAN") {
                  return person.primaryRole === "District Director" ||
                    !person.primaryRole
                    ? "National Director"
                    : person.primaryRole;
                }
                // For other districts, show primaryRole or fallback to "District Director"
                return person.primaryRole || "District Director";
              })()}
            </div>
          )}
        </div>
      </div>
      {/* Person Tooltip - hide when parent is showing centralized tooltip */}
      {!centralizedTooltipOpen &&
        !hideDetails &&
        isHovered &&
        tooltipPos &&
        person &&
        (clickTooltipOpen ||
          mobileTooltipOpen ||
          personNeed ||
          person.notes ||
          person.depositPaid) && (
          <>
            {(isMobile && mobileTooltipOpen) || clickTooltipOpen ? (
              <div
                role="button"
                tabIndex={0}
                aria-label="Close tooltip"
                className="fixed inset-0 z-[99998] bg-black/20"
                onClick={() =>
                  clickTooltipOpen
                    ? dismissClickTooltip()
                    : dismissMobileTooltip()
                }
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === "Escape")
                    clickTooltipOpen
                      ? dismissClickTooltip()
                      : dismissMobileTooltip();
                }}
              />
            ) : null}
            <PersonTooltip
              person={person}
              need={
                personNeed
                  ? {
                      type: personNeed.type,
                      description: personNeed.description,
                      amount: personNeed.amount,
                      isActive: personNeed.isActive,
                    }
                  : null
              }
              position={tooltipPos}
              centered={isMobile || clickTooltipOpen}
              onEdit={
                clickTooltipOpen || mobileTooltipOpen
                  ? handleEditFromTooltip
                  : undefined
              }
            />
          </>
        )}
    </>
  );
}
